const MIN_USABLE_DATA_URL_LENGTH = 100;

function isUsableImageDataUrl(dataUrl) {
  return (
    typeof dataUrl === "string" &&
    dataUrl.startsWith("data:image/") &&
    dataUrl.length > MIN_USABLE_DATA_URL_LENGTH
  );
}

function freezeChartForCapture(chartInstance) {
  if (chartInstance?.isFacetGridComposite) {
    const instances = chartInstance.getChartInstances?.() ?? [];
    instances.forEach((instance) => {
      if (typeof instance?.update === "function") {
        try {
          instance.update("none");
        } catch (_) {
          /* keep going; capture may still succeed */
        }
      }
    });
    return;
  }

  if (typeof chartInstance?.update === "function") {
    try {
      chartInstance.update("none");
    } catch (_) {
      /* keep going; capture may still succeed */
    }
  }
}

function readCanvasDataUrl(canvas) {
  if (!canvas || canvas.width <= 0 || canvas.height <= 0) {
    return "";
  }
  if (typeof canvas.toDataURL !== "function") {
    return "";
  }
  try {
    const dataUrl = canvas.toDataURL("image/png", 1);
    return isUsableImageDataUrl(dataUrl) ? dataUrl : "";
  } catch (_) {
    return "";
  }
}

function readChartImageDataUrl(chartInstance) {
  if (typeof chartInstance?.toBase64Image === "function") {
    try {
      const dataUrl = chartInstance.toBase64Image("image/png", 1);
      if (isUsableImageDataUrl(dataUrl)) {
        return dataUrl;
      }
    } catch (_) {
      /* fall through to canvas.toDataURL */
    }
  }

  return readCanvasDataUrl(chartInstance?.canvas);
}

export function chartInstanceHasRenderableCanvas(chartInstance) {
  if (!chartInstance) {
    return false;
  }
  if (chartInstance.isFacetGridComposite) {
    const canvases = (chartInstance.getChartInstances?.() ?? [])
      .map((instance) => instance?.canvas)
      .filter(Boolean);
    return canvases.length > 0 && canvases.every((canvas) => canvas.width > 0 && canvas.height > 0);
  }
  const canvas = chartInstance.canvas;
  return Boolean(canvas && canvas.width > 0 && canvas.height > 0);
}

/**
 * @param {Record<string, import('chart.js').Chart | undefined>} chartRefsByKey
 * @param {string[]} includedChartKeys
 * @param {{ timeoutMs?: number, pollMs?: number }} [options]
 */
export async function waitForPricingReportChartCanvasesReady(
  chartRefsByKey,
  includedChartKeys,
  { timeoutMs = 2000, pollMs = 50 } = {}
) {
  const startedAt = Date.now();
  const keys = includedChartKeys || [];

  while (Date.now() - startedAt < timeoutMs) {
    const ready = keys.every((chartKey) =>
      chartInstanceHasRenderableCanvas(chartRefsByKey?.[chartKey])
    );
    if (ready) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, pollMs));
  }

  return keys.every((chartKey) => chartInstanceHasRenderableCanvas(chartRefsByKey?.[chartKey]));
}

/**
 * @param {Record<string, import('chart.js').Chart | undefined>} chartRefsByKey
 * @param {string[]} includedChartKeys
 * @returns {Record<string, string>}
 */
export function capturePricingReportChartImagesForPrint(chartRefsByKey, includedChartKeys) {
  const chartImagesByKey = {};

  for (const chartKey of includedChartKeys) {
    const chartInstance = chartRefsByKey?.[chartKey];
    if (!chartInstance) {
      continue;
    }

    freezeChartForCapture(chartInstance);
    const dataUrl = readChartImageDataUrl(chartInstance);
    if (dataUrl) {
      chartImagesByKey[chartKey] = dataUrl;
    }
  }

  return chartImagesByKey;
}
